import React from "react";
import {
    useMutation,
    UseMutationOptions,
    UseMutationResult,
} from "@tanstack/react-query";

import { AuthContext } from "@contexts/auth";
import { useNavigation, useNotification } from "@hooks";

import { IAuthContext, TRegisterData } from "../../../interfaces";

export type UseRegisterProps<TVariables> = {
    mutationOptions?: Omit<
        UseMutationOptions<TRegisterData, Error, TVariables, unknown>,
        "mutationFn" | "onError" | "onSuccess"
    >;
};

/**
 * `useRegister` calls `register` method from {@link https://refine.dev/docs/api-references/providers/auth-provider `authProvider`} under the hood.
 *
 * @see {@link https://refine.dev/docs/core/hooks/auth/useRegister} for more details.
 *
 * @typeParam TData - Result data of the query
 * @typeParam TVariables - Values for mutation function. default `{}`
 *
 */
export const useRegister = <TVariables = {}>({
    mutationOptions,
}: UseRegisterProps<TVariables> = {}): UseMutationResult<
    TRegisterData,
    Error,
    TVariables,
    unknown
> => {
    const { replace } = useNavigation();
    const { register: registerFromContext } =
        React.useContext<IAuthContext>(AuthContext);

    const { close, open } = useNotification();

    const queryResponse = useMutation<
        TRegisterData,
        Error,
        TVariables,
        unknown
    >(["useRegister"], registerFromContext, {
        onSuccess: (redirectPathFromAuth) => {
            if (redirectPathFromAuth !== false) {
                if (redirectPathFromAuth) {
                    replace(redirectPathFromAuth);
                } else {
                    replace("/");
                }
            }
            close?.("register-error");
        },
        onError: (error: any) => {
            open?.({
                message: error?.name || "Register Error",
                description: error?.message || "Error while registering",
                key: "register-error",
                type: "error",
            });
        },
        ...mutationOptions,
    });

    return queryResponse;
};
